/**
 * A panel the menu opens over itself, and takes back down again.
 *
 * The character screen is a screen: a level, a bar, four stats, a perk tree, a comparison and a
 * respec. Sitting it in the menu column made the menu as long as the thing a player opened the game
 * to press. It is one press away instead, and the menu keeps its shape.
 *
 * The content element is handed in and handed back, never cloned and never disposed here. It
 * belongs to whoever built it, which for the upgrade panel is the shell, and it outlives this
 * sheet by design: the same element mounts again the next time the sheet opens.
 *
 * Mounted inside the menu rather than on the body, so that `inert` on the menu reaches it and a
 * disposed menu cannot leave a sheet behind.
 */

import { el } from '@/ui/dom';
import { injectMenuSheetStyles } from '@/ui/menu/menuSheetStyles';

export interface MenuSheet {
  /** Idempotent: closing by scrim, by the button and by a disposed menu all land here. */
  dispose(): void;
}

export interface MenuSheetOptions {
  /** Where the sheet mounts. The menu root, so the menu's `inert` covers it. */
  readonly host: HTMLElement;
  readonly title: string;
  readonly closeLabel: string;
  /** Reparented in on open and detached on close, still alive either way. */
  readonly content: HTMLElement;
  /** Told after the sheet is down, so the opener can forget it and re-enable itself. */
  readonly onClose: () => void;
}

export function openMenuSheet(options: MenuSheetOptions): MenuSheet {
  const removeStyles = injectMenuSheetStyles();

  // Where the press came from, so the keyboard lands back on it rather than at the top of the menu.
  const opener = document.activeElement;

  const root = el('div', 'sheet');
  const card = el('div', 'sheet-card');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');

  const close = el('button', 'sheet-close', options.closeLabel);
  close.type = 'button';

  const head = el('div', 'sheet-head');
  head.append(el('h2', 'sheet-title', options.title), close);

  const body = el('div', 'sheet-body');
  body.append(options.content);
  card.append(head, body);
  root.append(card);

  let down = false;

  const shut = (): void => {
    if (down) return;
    down = true;
    document.removeEventListener('keydown', onKey, true);
    // Detached rather than removed with the tree: the caller still owns it and will mount it again.
    options.content.remove();
    root.remove();
    removeStyles();
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    options.onClose();
  };

  function onKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    shut();
  }

  // Capture, because the sheet is the outermost thing on screen and should answer Escape before
  // anything inside it that has not already stopped the event.
  document.addEventListener('keydown', onKey, true);

  close.addEventListener('click', shut);
  root.addEventListener('click', (event: MouseEvent) => {
    if (event.target === root) shut();
  });

  options.host.append(root);
  close.focus();

  return { dispose: shut };
}
